import { Component, OnInit, ViewChild, ElementRef, ChangeDetectorRef } from '@angular/core';
import { of, throwError } from 'rxjs';
import { AbstractControl, UntypedFormGroup, Validators, UntypedFormControl, UntypedFormBuilder, NgForm } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { UtilsService, PersonalService, FeedMessageService, LoadingService, UserPinSessionService, UserPinAuthService, CustomValidators, UserPinService, showSuccess } from '@services/index.service';
import { MatDialog } from '@angular/material/dialog';
import { environment } from '@environments/environment';
import { catchError, finalize, map, tap } from 'rxjs/operators';
import { Consumer, CustomdataResponse, ErrorCode, LoginByCertificateRequest, MessageType, Response, CheckDomainRequest, LoginRequest, LoginStep, AngularPaths, AuthUserPinType, LocalStorageKeys, UserPinConfig, DomainType, CreateUserPinRequest, CodeUserPinRequest } from '@models/index.model';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { BackgroundColor, InAppBrowser, OpenWebViewOptions, ToolBarType, UrlEvent } from '@capgo/inappbrowser';

@Component({
    selector: 'app-auth-user-pin',
    templateUrl: './auth-user-pin.component.html',
    styleUrls: ['./auth-user-pin.component.scss'],
    standalone: false
})
export class AuthUserPinComponent implements OnInit {
  public theme = environment.THEME;
  public currentVersion = environment.VERSION;
  public hide: boolean = true;
  public loginForm: UntypedFormGroup;
  // Custom login screen settings.
  public customImgSrc: string;
  public customText: string;
  // Login input fields.
  private formSubmitAttempt: boolean;

  // Login steps management.
  public loginStep: LoginStep;
  public loadingCustomData = false;
  public loadingLogin = false;
  public loadingLoginCert = false;
  public isMobile: boolean;
  public ssoLoginButtons = [];
  public ssoLoginClickedButton = '';
  public certificateDomainsConfigured: boolean = false;
  @ViewChild('password') public set focusPassword(password: ElementRef) {
    if (password) {
      password.nativeElement.focus();
      this._cdRef.detectChanges();
    }
  }
  public currentConsumer: Consumer = Consumer.GLOBAL_PERSONAL_AREA;

  constructor(
    public dialog: MatDialog,
    public translate: TranslateService,
    private route: ActivatedRoute,
    private router: Router,
    public _utils: UtilsService,
    private _fb: UntypedFormBuilder,
    private _userPinSession: UserPinSessionService,
    private _personalSvc: PersonalService,
    private _titleService: Title,
    private _loading: LoadingService,
    private _userPinAuth: UserPinAuthService,
    private _notify: FeedMessageService,
    private _cdRef: ChangeDetectorRef
  ) {
    this.theme = this._utils.titleCaseWord(this.theme);
    this.isMobile = environment.platform === 'mobile';
  }

  public ngOnInit() {
    this._loading.showGlobalLoading();
    this.translate.get('GLOBAL.ACCOUNT-EDIT-INFO-3-PIN').subscribe(res => {
      this._titleService.setTitle(`${res} - ${this.theme}`);
    });
    this.dialog.closeAll();
    this.currentConsumer = Consumer.GLOBAL_PERSONAL_AREA;
    this._userPinSession.removeBrowserSession();
    this.route.params.subscribe((params: Params) => {
      if (params.userPinConfig) {
        // SET or RESET
        const cleanData = params.userPinConfig
        localStorage.setItem(LocalStorageKeys.USER_PIN_REDIRECT_ID, cleanData);
        const userPinconfig: UserPinConfig = this._utils.parseUserPreferenceConfig(cleanData);
        console.log(userPinconfig);
        if ((userPinconfig.dt === DomainType.OAUTH2 || userPinconfig.dt === DomainType.SAML2) && userPinconfig.dn) {
          this.loginWithSso(userPinconfig.dn);
        } else {
          if (environment.production) {
            window.history.replaceState({}, '',`${window.location.origin}/personal/${AngularPaths.USER_PIN}/`);
          } else {
            window.history.replaceState({}, '',`${window.location.origin}/${AngularPaths.USER_PIN}/`);
          }
          this._loading.hideGlobalLoading();
        }
      } else {
        this.close();
      }
    });
    this.loginStep = LoginStep.USERNAME;
    this.loginForm = this._initLoginForm();
    this._getCustomData();
    this.getListDomains();
  }

  public getListDomains() {
    this._userPinAuth
      .personalSSODomain()
      .pipe(
        catchError(err => {
          this.resetForm();
          return throwError(() => err);
        }),
        finalize(() => {
        })
      )
      .subscribe((res: Response<any>) => {
        if (res.data) {
          this.ssoLoginButtons = res.data.ssoLoginButtons;
          this.certificateDomainsConfigured = res.data.certificateDomainsConfigured;
        }
      });
  }

  private _getCustomData() {
    this.loadingCustomData = true;
    this._personalSvc
      .getCustomData()
      .pipe(
        map((res: Response<CustomdataResponse>) => this._utils.mapResponseData<CustomdataResponse>(res)),
        finalize(() => {
          this.loadingCustomData = false;
        })
      )
      .subscribe((res: CustomdataResponse) => {
        this.customImgSrc = res.b64ImageData ? this._utils.parseText64ToImgURL(res.b64ImageData, 'png') : null;
        this.customText = res.customText ? res.customText : null;
      });
  }

  private _initLoginForm(): UntypedFormGroup {
    return this._fb.group({
      username: [null, Validators.required],
      password: [null, Validators.required],
      consumer: [this.currentConsumer],
      domainToReturn: [window.location.origin],
    });
  }

  public isFieldInvalid(field: string) {
    return (
      (!this.loginForm.get(field).valid && this.loginForm.get(field).touched) ||
      (this.loginForm.get(field).untouched && this.formSubmitAttempt)
    );
  }

  public onSubmitUsername({ username, consumer, domainToReturn }: CheckDomainRequest) {
    this.loadingLogin = true;
    this._userPinAuth
      .checkDomain({
        username: username,
        consumer: consumer,
        domainToReturn: domainToReturn,
      })
      .pipe(
        catchError(err => {
          this.resetForm();
          return throwError(() => err);
        }),
        finalize(() => {
          this.loadingLogin = false;
        })
      )
      .subscribe((res: Response<any>) => {
        if (res.errorCode === ErrorCode.OK) {
          this.loginStep = LoginStep.PASSWORD;
        }
        if (res.errorCode === ErrorCode.REDIRECT) {
          if (!res.data) {
            this._notify.feedBackResult(MessageType.ERROR, this.translate.instant('GLOBAL.ERROR-RETRY'));
            return;
          } else {
            if (this.isMobile) {
              // Signtrust APP open BrowserInApp
              this.openSSOAuth(res.data.url);
            } else {
              window.location.href = res.data.url;
            }
          }
        }
      });
  }

  public loginWithSso(domain) {
    this.loadingLogin = true;
    this._userPinAuth
      .checkDomain({
        username: domain,
        consumer: this.currentConsumer,
        domainToReturn: window.location.origin,
      })
      .pipe(
        catchError(err => {
          this.resetForm();
          return throwError(() => err);
        }),
        finalize(() => {
          this.loadingLogin = false;
        })
      )
      .subscribe((res: Response<any>) => {
        if (res.errorCode === ErrorCode.REDIRECT) {
          if (!res.data) {
            this._notify.feedBackResult(MessageType.ERROR, this.translate.instant('GLOBAL.ERROR-RETRY'));
            return;
          } else {
            if (this.isMobile) {
              // Signtrust APP open BrowserInApp
              this.openSSOAuth(res.data.url);
            } else {
              window.location.href = res.data.url;
            }
          }
        }
      });
  }

  public async openSSOAuth(url: string): Promise<void> {
    let webViewInApp: OpenWebViewOptions = {
      url: url,
      // headers: Headers,
      // shareDisclaimer: DisclaimerOptions,
      toolbarType: ToolBarType.DEFAULT,
      shareSubject: '',
      title: 'Auth SSO - User PIN',
      backgroundColor: BackgroundColor.BLACK,
      activeNativeNavigationForWebview: false,
      disableGoBackOnNativeApplication: true,
      isPresentAfterPageLoad: false,
      isInspectable: false,
      isAnimated: true,
      showReloadButton: false,
      closeModal: false,
      closeModalTitle: 'Close',
      closeModalDescription: 'Are you sure you want to close this window?',
      closeModalOk: 'Close',
      closeModalCancel: 'Cancel',
      visibleTitle: true,
      toolbarColor: '#ffffff',
      showArrow: false
    }
    await InAppBrowser.openWebView(webViewInApp);

    InAppBrowser.addListener('urlChangeEvent', (info: UrlEvent) => {
      console.log('InAppBrowser urlChangeEvent: ', info.url);
      if (info.url.includes('/personal/sso-user-pin/')) {
        console.log('Auth LoginByToken Detect: ', info.url);
        // Extraer el código o token de la URL
        const urlSplit: string[] = info.url.split('sso-user-pin/');
        const tempToken = urlSplit[urlSplit.length-1];
        console.log('Call AuthAPI LoginByToken:', tempToken);
        this.router.navigate([`/${AngularPaths.SSO_USER_PIN}/${tempToken}`]);
        // Cierra el navegador
        InAppBrowser.close();
      }
      if (info.url.includes('/personal/error/')) {
        console.log('Error LoginByToken Detect: ', info.url);
        // Extraer el código o token de la URL
        const urlSplit: string[] = info.url.split('error/');
        const customError = urlSplit[urlSplit.length-1];
        console.log('Error on IDP Auth LoginByToken:', customError);
        this.router.navigate([`/${AngularPaths.ERROR}/${customError}`]);
        // Cierra el navegador
        InAppBrowser.close();
      }
    });

    InAppBrowser.addListener('closeEvent', (info: UrlEvent) => {
      console.log('InAppBrowser closeEvent: ', info.url);
      InAppBrowser.removeAllListeners();
    });
  }

  public onSubmitPassword({ username, password, consumer }: LoginRequest) {
    const passwordControl: AbstractControl = this.loginForm.get('password');
    passwordControl.setValue(passwordControl.value.trim());
    this.loadingLogin = true;
    this._userPinSession
      .initLocalUserSession({ username, password, consumer })
      .pipe(
        tap(_ => this.resetForm()),
        catchError(err => {
          this._resetPassword();
          this.formSubmitAttempt = true;
          return throwError(() => err);
        }),
        finalize(() => {
          this.loadingLogin = false;
        })
      )
      .subscribe();
  }

  public onLoginByCertificate() {
    this.loadingLoginCert = true;
    const loginByCertificateRequest: LoginByCertificateRequest = { consumer: this.currentConsumer };
    this._userPinSession
      .initCertificateSession(loginByCertificateRequest)
      .pipe(
        catchError(err => {
          return throwError(() => err);
        }),
        finalize(() => {
          this.resetForm();
          this.formSubmitAttempt = true;
          this.loadingLoginCert = false;
        })
      )
      .subscribe();
  }

  public resetForm() {
    this._resetUsername();
    this._resetPassword();
    this.loginStep = LoginStep.USERNAME;
  }

  private _resetUsername() {
    const userNameControl: AbstractControl = this.loginForm.get('username');
    userNameControl.reset();
    userNameControl.updateValueAndValidity();
  }

  private _resetPassword() {
    const passwordControl: AbstractControl = this.loginForm.get('password');
    passwordControl.reset();
    passwordControl.updateValueAndValidity();
  }

  public close() {
    this._userPinSession.removeBrowserSession();
    window.close();
  }
}
